import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Crown, Gift, Check, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RedeemPage() {
  const { token } = useParams();
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redeeming, setRedeeming] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInviteInfo();
  }, [token]);

  const fetchInviteInfo = async () => {
    try {
      const response = await axios.get(`${API}/invite/${token}`);
      setInviteInfo(response.data);
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid or expired invite link');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeem = async () => {
    if (!user) {
      // Redirect to register with return URL
      navigate(`/register?redirect=/redeem/${token}`);
      return;
    }

    setRedeeming(true);
    try {
      await axios.post(`${API}/invite/${token}/redeem`);
      toast.success('Welcome! Your free membership is now active!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to redeem invite');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div 
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1550053808-52a75a05955d?crop=entropy&cs=srgb&fm=jpg&q=85)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      <div className="absolute inset-0 bg-[#050505]/90" />
      
      <div className="relative w-full max-w-md">
        <div className="text-center mb-10">
          <Link to="/" className="inline-flex items-center gap-3 mb-8">
            <Crown className="w-10 h-10 text-gold-400" />
            <span className="font-heading text-2xl font-bold text-white">House of Royalty</span>
          </Link>
        </div>

        {error ? (
          <div className="glass-heavy p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h1 className="text-2xl font-heading font-bold text-white mb-4">Invalid Invite</h1>
            <p className="text-zinc-400 mb-8">{error}</p>
            <Link to="/" className="btn-outline inline-block">
              Go to Homepage
            </Link>
          </div>
        ) : (
          <div className="glass-heavy p-8 text-center">
            <Gift className="w-16 h-16 text-gold-400 mx-auto mb-6" />
            <h1 className="text-2xl font-heading font-bold text-white mb-2">You're Invited!</h1>
            <p className="text-zinc-400 mb-8">
              You've been granted exclusive access to House of Royalty
            </p>

            <div className="bg-black/30 border border-gold-500/30 p-6 mb-8">
              <p className="text-gold-400 uppercase tracking-widest text-xs mb-2">Free Membership</p>
              <h2 className="text-3xl font-heading font-bold gold-text mb-2">
                {inviteInfo?.tier_name}
              </h2>
              <p className="text-zinc-400 text-sm mb-4">
                {inviteInfo?.duration_days} days of full access
              </p>
              
              {inviteInfo?.tier?.features && (
                <ul className="text-left space-y-2">
                  {inviteInfo.tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-zinc-300 text-sm">
                      <Check className="w-4 h-4 text-gold-400 flex-shrink-0 mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {inviteInfo?.remaining_uses <= 3 && (
              <p className="text-amber-400 text-sm mb-4">
                Only {inviteInfo.remaining_uses} invite{inviteInfo.remaining_uses !== 1 ? 's' : ''} remaining!
              </p>
            )}

            <button
              onClick={handleRedeem}
              disabled={redeeming}
              className="btn-gold w-full"
              data-testid="redeem-invite-btn"
            >
              {redeeming ? 'Activating...' : user ? 'Claim Your Free Access' : 'Sign Up to Claim'}
            </button>

            {!user && (
              <p className="text-zinc-500 text-sm mt-4">
                Already have an account?{' '}
                <Link to={`/login?redirect=/redeem/${token}`} className="text-gold-400 hover:text-gold-300">
                  Sign in
                </Link>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
